import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  notifications,
  notificationKeys,
  buildNotificationHubConnection,
  type NotificationDto,
  type NotificationListResult,
} from "@/lib/notifications";
import { getAccessToken } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";
import { NotificationBell } from "@/components/notification-bell";
import { outstandingCounts, outstandingCountsKeys } from "@/lib/outstanding-counts";
import { CounterPill } from "@/components/counter-pill";

function WiredNotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: notificationKeys.list,
    queryFn: () => notifications.list(),
  });
  const items = data?.items ?? [];

  const markRead = useMutation({
    mutationFn: (id: number) => notifications.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.list }),
  });
  const markAll = useMutation({
    mutationFn: () => notifications.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.list }),
  });

  useEffect(() => {
    const conn = buildNotificationHubConnection(async () => getAccessToken());
    conn.on("NotificationCreated", (n: NotificationDto) => {
      qc.setQueryData<NotificationListResult>(notificationKeys.list, (prev) => {
        const existing = prev?.items ?? [];
        if (existing.some((x) => x.id === n.id)) return prev;
        return { items: [n, ...existing], unreadCount: (prev?.unreadCount ?? 0) + 1 };
      });
      toast.info(n.title);
    });
    conn.start().catch(() => { /* withAutomaticReconnect handles transient failures */ });
    return () => { void conn.stop(); };
  }, [qc]);

  return (
    <NotificationBell
      notifications={items}
      onItemClick={(n) => {
        markRead.mutate(n.id);
        if (n.linkUrl) void navigate(n.linkUrl);
      }}
      onMarkAllRead={() => markAll.mutate()}
    />
  );
}

export function TopBar() {
  const navigate = useNavigate();

  const { data: emailsOutstanding = 0 } = useQuery({
    queryKey: outstandingCountsKeys.emails,
    queryFn: () => outstandingCounts.emails(),
    staleTime: 60_000,
  });
  const { data: enquiriesOutstanding = 0 } = useQuery({
    queryKey: outstandingCountsKeys.enquiries,
    queryFn: () => outstandingCounts.enquiries(),
    staleTime: 60_000,
  });

  return (
    <header
      role="banner"
      className="flex h-10 items-center justify-between border-b border-border bg-card px-4"
    >
      <div id="topbar-left-portal" className="flex items-center gap-4"></div>
      <div className="flex items-center gap-3">
        <div id="topbar-right-portal" className="flex items-center gap-2 empty:hidden border-r border-border pr-3"></div>
      <CounterPill
        count={emailsOutstanding}
        label="Emails Outstanding"
        variant="info"
        onClick={() => { void navigate("/jobs/emails?filter=outstanding"); }}
      />
      <CounterPill
        count={enquiriesOutstanding}
        label="Enquiries Outstanding"
        variant={enquiriesOutstanding > 0 ? "warning" : "info"}
        onClick={() => { void navigate("/enquiries?filter=mine&status=open"); }}
      />
      <WiredNotificationBell />
      </div>
    </header>
  );
}
